import { useState } from "react";

import { IDashboardStats } from "../../models/DashboardStats";
import { VehicleAcquisitionMetricKey } from "../../models/VehicleAcquisitionDrilldown";
import openInNewTab from "../../utils/openInNewTab";
import PriceFormat from "../../utils/priceFormat";
import DashboardTile from "./DashboardTile";
import VehicleAcquisitionDrilldownModal from "./VehicleAcquisitionDrilldownModal";

interface IVehicleAcquisitionTableProps {
  dashboardStats: IDashboardStats | undefined;
  span?: 1 | 2;
}

type DashboardStatsExtended = IDashboardStats & {
  customersAwaitingCall?: number;
  callsLoggedToday?: number;
};

type RowConfig = {
  metricKey: VehicleAcquisitionMetricKey;
  valueKey: keyof DashboardStatsExtended;
  label: string;
  bgClass: string;
  textClass: string;
};

const rowConfig: RowConfig[] = [
  {
    metricKey: "unread-messages",
    valueKey: "unreadMessages",
    label: "Unread Messages",
    bgClass: "bg-blue-50",
    textClass: "text-blue-700",
  },
  {
    metricKey: "customers-awaiting-call",
    valueKey: "customersAwaitingCall",
    label: "Customers Awaiting Call",
    bgClass: "bg-amber-50",
    textClass: "text-amber-700",
  },
  {
    metricKey: "vehicles-booked-today",
    valueKey: "bookedToday",
    label: "Vehicles Booked Today",
    bgClass: "bg-emerald-50",
    textClass: "text-emerald-700",
  },
  {
    metricKey: "calls-logged-today",
    valueKey: "callsLoggedToday",
    label: "Calls Logged Today",
    bgClass: "bg-violet-50",
    textClass: "text-violet-700",
  },
];

function formatValue(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return String(value);
}

function getBookedByUserRows(stats: DashboardStatsExtended): { user: string; count: number; totalValue: number }[] {
  const activity = stats.userActivity ?? {};
  return Object.entries(activity)
    .filter(([key]) => key !== "Total")
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([user, u]) => ({
      user,
      count: u?.booked ?? 0,
      totalValue: u?.bookedValue ?? 0,
    }))
    .filter((row) => row.count > 0);
}

export default function VehicleAcquisitionTable({ dashboardStats, span = 2 }: IVehicleAcquisitionTableProps) {
  const [selectedMetric, setSelectedMetric] = useState<RowConfig | null>(null);
  const [drilldownOpen, setDrilldownOpen] = useState(false);

  if (!dashboardStats) return null;

  const stats = dashboardStats as DashboardStatsExtended;
  const bookedByUserRows = getBookedByUserRows(stats);

  const handleMetricRowClick = (row: RowConfig) => {
    setSelectedMetric(row);
    setDrilldownOpen(true);
  };

  return (
    <DashboardTile span={span}>
      <div className="p-3">
        <VehicleAcquisitionDrilldownModal
          open={drilldownOpen}
          setOpen={setDrilldownOpen}
          metric={selectedMetric?.metricKey ?? null}
          metricLabel={selectedMetric?.label ?? ""}
          onRowClick={(row) => {
            openInNewTab(`/vehicles/${row.vehicleId}`);
          }}
        />

        <div className="mb-3 w-full">
          <h3 className="prose font-semibold text-gray-900">Vehicle Acquisition</h3>
          <p className="text-sm text-gray-500 mt-0.5">Messages, calls and booking targets</p>
        </div>

        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="pb-2.5 pt-2.5 pl-4 pr-4 font-semibold text-gray-800">Metric</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">Value</th>
              </tr>
            </thead>
            <tbody>
              {rowConfig.map((row, index) => {
                const value = stats[row.valueKey] as number | null | undefined;
                return (
                  <tr
                    key={index}
                    className={`font-medium transition-colors ${row.bgClass} cursor-pointer hover:brightness-95`}
                    onClick={() => handleMetricRowClick(row)}
                  >
                    <td className={`py-2.5 pl-4 pr-4 ${row.textClass}`}>{row.label}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${row.textClass}`}>{formatValue(value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Booked in by user</h4>
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="pb-2 pt-2 pl-3 pr-3 font-semibold text-gray-800">User</th>
                  <th className="pb-2 pt-2 pr-3 text-right font-semibold text-gray-800">Vehicles Booked</th>
                  <th className="pb-2 pt-2 pr-3 text-right font-semibold text-gray-800">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {bookedByUserRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-3 pl-3 pr-3 text-center text-gray-500 text-sm">
                      No data
                    </td>
                  </tr>
                ) : (
                  bookedByUserRows.map((row) => (
                    <tr key={row.user} className="font-medium border-b border-gray-100 last:border-0 hover:bg-gray-50/50">
                      <td className="py-2 pl-3 pr-3 text-gray-700">{row.user}</td>
                      <td className="py-2 pr-3 text-right font-bold text-gray-900 tabular-nums">{row.count}</td>
                      <td className="py-2 pr-3 text-right font-bold text-gray-900 tabular-nums">{PriceFormat(row.totalValue)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardTile>
  );
}
